/**
 * Page-local "Need help?" dialog for Sync Analytics.
 * Does not replace the global sidebar Help modal.
 */

import React, { useEffect, useRef, useState } from 'react';
import { helpApi } from '../../api/helpApi';

interface Props {
    open: boolean;
    onClose: () => void;
    practiceName?: string;
    locationId?: string;
}

const MAX_CHARS = 4000;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export const AnalyticsHelpDialog: React.FC<Props> = ({
    open,
    onClose,
    practiceName,
    locationId,
}) => {
    const [message, setMessage] = useState('');
    const [image, setImage] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [imagesEnabled, setImagesEnabled] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sent, setSent] = useState(false);
    const [imageSkippedNote, setImageSkippedNote] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (!open) return;
        setMessage('');
        setImage(null);
        setPreviewUrl(null);
        setError(null);
        setSent(false);
        setImageSkippedNote(false);
        setSending(false);
        helpApi.status().then(s => setImagesEnabled(s.images_enabled)).catch(() => setImagesEnabled(false));
        // Focus after open paint.
        const t = window.setTimeout(() => textareaRef.current?.focus(), 50);
        return () => window.clearTimeout(t);
    }, [open]);

    useEffect(() => {
        if (!image) {
            setPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(image);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [image]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !sending) onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose, sending]);

    if (!open) return null;

    const onPickImage = (file: File | null) => {
        setError(null);
        if (!file) {
            setImage(null);
            return;
        }
        if (!file.type.startsWith('image/')) {
            setError('Please choose an image file (PNG, JPEG, GIF, or WebP).');
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setError('Image must be 5 MB or smaller.');
            return;
        }
        setImage(file);
    };

    const submit = async () => {
        const text = message.trim();
        if (!text) {
            setError('Please write a short message.');
            return;
        }
        setSending(true);
        setError(null);
        try {
            const result = await helpApi.submitAnalyticsHelp({
                message: text,
                practiceName,
                locationId,
                page: 'Sync Analytics',
                image,
            });
            setSent(true);
            setImageSkippedNote(!!result.image_skipped);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to send');
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/35"
            onClick={() => { if (!sending) onClose(); }}
            role="presentation"
        >
            <div
                className="w-full max-w-[480px] bg-mapper-white border border-mapper-border rounded-[14px] shadow-lg overflow-hidden"
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="analytics-help-title"
            >
                <div className="flex items-start justify-between gap-3 px-[18px] pt-[16px] pb-[12px] border-b border-mapper-border">
                    <div className="min-w-0">
                        <h2 id="analytics-help-title" className="text-[16px] font-extrabold tracking-[-0.2px]">
                            Need help?
                        </h2>
                        <p className="text-[12.5px] text-mapper-muted mt-1 leading-snug">
                            Tell us what&apos;s wrong on Sync Analytics — we&apos;ll send it to the team in Slack.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={sending}
                        className="w-[28px] h-[28px] rounded-[8px] text-mapper-muted-3 hover:bg-[#F5F7F9] hover:text-mapper-body-text shrink-0"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div className="px-[18px] py-4">
                    {sent ? (
                        <div className="py-3">
                            <div className="text-[14.5px] font-bold text-mapper-teal-dark mb-1.5">Message sent</div>
                            <p className="text-[13px] text-mapper-muted leading-relaxed">
                                Thanks — the team has it in Slack
                                {practiceName ? ` for ${practiceName}` : ''}.
                            </p>
                            {imageSkippedNote && (
                                <p className="text-[12.5px] text-[#7A5A15] mt-2 leading-relaxed">
                                    Your note went through, but the screenshot couldn&apos;t be attached
                                    (image delivery isn&apos;t fully configured on the server yet).
                                </p>
                            )}
                            <button
                                type="button"
                                onClick={onClose}
                                className="mt-4 px-[14px] py-2 rounded-[9px] bg-mapper-teal text-white text-[13px] font-semibold"
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <>
                            <label className="block text-[12px] font-semibold text-mapper-body-text mb-1.5" htmlFor="analytics-help-msg">
                                How can we help?
                            </label>
                            <textarea
                                id="analytics-help-msg"
                                ref={textareaRef}
                                value={message}
                                onChange={e => setMessage(e.target.value.slice(0, MAX_CHARS))}
                                rows={5}
                                disabled={sending}
                                placeholder="e.g. Failed counts look wrong for this practice after yesterday…"
                                className="w-full resize-y min-h-[110px] rounded-[10px] border border-mapper-border-2 px-3 py-2.5 text-[13.5px] text-mapper-text placeholder:text-mapper-muted-3 focus:outline-none focus:border-mapper-teal"
                            />
                            <div className="flex justify-end mt-1">
                                <span className="text-[11px] text-mapper-muted-3 font-mono">
                                    {message.length}/{MAX_CHARS}
                                </span>
                            </div>

                            <div className="mt-3">
                                <div className="text-[12px] font-semibold text-mapper-body-text mb-1.5">
                                    Screenshot <span className="font-medium text-mapper-muted-3">(optional)</span>
                                </div>
                                {!imagesEnabled && (
                                    <p className="text-[11.5px] text-mapper-muted-3 mb-2 leading-snug">
                                        You can still attach a screenshot — we&apos;ll include it when the server is set up for image delivery.
                                    </p>
                                )}
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/gif,image/webp"
                                    className="hidden"
                                    onChange={e => onPickImage(e.target.files?.[0] ?? null)}
                                />
                                {!image ? (
                                    <button
                                        type="button"
                                        disabled={sending}
                                        onClick={() => fileRef.current?.click()}
                                        className="px-3 py-2 rounded-[9px] border border-dashed border-mapper-border-2 text-[12.5px] font-semibold text-mapper-body-text hover:bg-[#F5F7F9]"
                                    >
                                        Attach image
                                    </button>
                                ) : (
                                    <div className="flex items-start gap-3 border border-mapper-border rounded-[10px] p-2.5">
                                        {previewUrl && (
                                            <img
                                                src={previewUrl}
                                                alt="Attachment preview"
                                                className="w-[72px] h-[72px] object-cover rounded-[8px] border border-mapper-border shrink-0"
                                            />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[12.5px] font-semibold truncate">{image.name}</div>
                                            <div className="text-[11px] text-mapper-muted-3 mt-0.5">
                                                {(image.size / 1024).toFixed(0)} KB
                                            </div>
                                            <button
                                                type="button"
                                                disabled={sending}
                                                onClick={() => {
                                                    setImage(null);
                                                    if (fileRef.current) fileRef.current.value = '';
                                                }}
                                                className="mt-1.5 text-[12px] font-semibold text-mapper-red"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {error && (
                                <div className="mt-3 text-[13px] text-mapper-red leading-snug">{error}</div>
                            )}

                            {(practiceName || locationId) && (
                                <p className="mt-3 text-[11.5px] text-mapper-muted-3 leading-snug">
                                    Includes practice context
                                    {practiceName ? `: ${practiceName}` : ''}
                                    {locationId ? ` (${locationId})` : ''}.
                                </p>
                            )}

                            <div className="flex items-center justify-end gap-2 mt-5">
                                <button
                                    type="button"
                                    disabled={sending}
                                    onClick={onClose}
                                    className="px-[14px] py-2 rounded-[9px] border border-mapper-border-2 text-[13px] font-semibold text-mapper-body-text"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={sending || !message.trim()}
                                    onClick={() => void submit()}
                                    className="px-[14px] py-2 rounded-[9px] bg-mapper-teal text-white text-[13px] font-semibold disabled:opacity-50"
                                >
                                    {sending ? 'Sending…' : 'Send to team'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
